import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { Attachment } from '@ee/common/models';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  loadDocumentByCase(caseId: string, attachmentId: string, preview: boolean = false, responseType: any = 'text'): Observable<string> {
    return this.http.get<string>(`${this.environment.api_prefix}api/document/case/${caseId}/attachments/${attachmentId}?preview=${preview}`, { responseType });
  }

  loadIntakeDocById(id: string, attachmentId: string, preview: boolean = false, responseType: any = 'text'): Observable<string> {
    return this.http.get<string>(`${this.environment.api_prefix}api/document/intake-evictions/${id}/attachments/${attachmentId}?preview=${preview}`, { responseType });
  }

  /** Signed URL for a PM (case-management) case attachment — original, or PDF when `preview`. */
  loadCaseMgmtDoc(caseId: string, attachmentId: string, preview: boolean = false, responseType: any = 'text'): Observable<string> {
    return this.http.get<string>(`${this.environment.api_prefix}api/document/case-mgmt/${caseId}/attachments/${attachmentId}?preview=${preview}`, { responseType });
  }

  /** Download a PM case attachment (its original file). */
  async downloadCaseMgmtAttachment(caseId: string, attachment: Attachment): Promise<void> {
    const splitPath = attachment.path.split('/');
    const fullFilename = splitPath[splitPath.length - 1];
    const dotIndex = fullFilename.lastIndexOf('.');
    const baseName = dotIndex > 0 ? fullFilename.substring(0, dotIndex) : fullFilename;
    const filename = attachment.original_filename || attachment.generated_filename || baseName;
    const url = await firstValueFrom(this.loadCaseMgmtDoc(caseId, attachment.id));
    this.saveDocumentFromUrl(this.withExtensionFromSignedUrl(filename, url), url);
  }

  /** Open a PM case attachment's PDF rendition in a new tab. */
  async previewCaseMgmtAttachment(caseId: string, attachmentId: string): Promise<void> {
    const url = await firstValueFrom(this.loadCaseMgmtDoc(caseId, attachmentId, true));
    window.open(url, '_blank');
  }

  /** Kick off the async zip of all of a PM case's documents (delivered via the documents-download SSE event). */
  downloadCaseManagementFiles(caseId: string): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/document/case-mgmt/${caseId}/download-all`, {});
  }

  /**
   * The server scopes the lookup to the caller's organization and lets genuinely public templates
   * through on its own, so there is no `public` flag to send — it used to be one, and trusting it
   * let a caller read another organization's template file.
   */
  loadDocumentTemplate(id: string, responseType: any = 'text'): Observable<string> {
    return this.http.get<string>(`${this.environment.api_prefix}api/document/templates/${id}`, { responseType });
  }

  loadInvoiceById(id: string, version: string = undefined, responseType: any = 'text'): Observable<string> {
    let apiPath = `${this.environment.api_prefix}api/document/invoices/${id}`;
    if (version) {
      apiPath = apiPath + `?version=${version}`;
    }
    return this.http.get<string>(apiPath, { responseType });
  }

  downloadCaseDocuments(files: { [caseId: string]: string[] }, groupByCase: boolean): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/document/case/attachments`, { files, group_by_case: groupByCase });
  }

  saveDocument(filename: string, data: any) {
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    link.remove();
  }

  saveDocumentFromUrl(filename: string, url: string) {
    // Fetch as blob to create a same-origin object URL. The `download` attribute on <a> tags
    // is ignored by browsers for cross-origin URLs (e.g., CloudFront signed URLs).
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(err => {
        console.error('Failed to download file:', err);
      });
  }

  /**
   * Appends the file extension from a presigned URL's S3 key to a friendly filename.
   * Attachment paths are stored without an extension (the concrete format only lives on the
   * S3 key), so the signed URL — not `attachment.path` — is the reliable extension source.
   */
  private withExtensionFromSignedUrl(name: string, signedUrl: string): string {
    const keyName = signedUrl.split('?')[0].split('/').pop() || '';
    const dotIndex = keyName.lastIndexOf('.');
    const extension = dotIndex > 0 ? keyName.substring(dotIndex) : '';
    // Comparing the suffix — not just whether a '.' exists — keeps the extension for names that
    // contain dots but no extension (e.g. "Template v1.2") and is case-insensitive ("Report.DOCX").
    if (extension && !name.toLowerCase().endsWith(extension.toLowerCase())) {
      return name + extension;
    }
    return name;
  }

  generateFilenameFromPath(attachment: Attachment, overrideExtension: string | null = null): string {
    const splitPath = attachment.path.split('/');
    const fullFilename = splitPath[splitPath.length - 1];
    const dotIndex = fullFilename.lastIndexOf('.');
    const extension = dotIndex > 0 ? fullFilename.substring(dotIndex + 1) : '';
    const baseName = dotIndex > 0 ? fullFilename.substring(0, dotIndex) : fullFilename;
    const filename = attachment.generated_filename ?? baseName;
    return `${filename}.${overrideExtension ?? extension}`;
  }

  downloadCaseFiles(caseIds: string[]): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/document/case-files`, caseIds);
  }

  downloadInvoices(invoiceIds: string[]): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/document/invoice-pdfs`, invoiceIds);
  }

  async loadAndSaveAttachmentByCase(caseId: string, attachment: Attachment, preview: boolean = false) {
    const splitPath = attachment.path.split('/');
    const fullFilename = splitPath[splitPath.length - 1];
    const dotIndex = fullFilename.lastIndexOf('.');
    const baseName = dotIndex > 0 ? fullFilename.substring(0, dotIndex) : fullFilename;
    const filename = attachment.generated_filename ?? baseName;
    const url = await firstValueFrom(this.loadDocumentByCase(caseId, attachment.id, preview));
    this.saveDocumentFromUrl(this.withExtensionFromSignedUrl(filename, url), url);
  }

  async loadAndSaveIntakeDoc(caseId: string, attachment: Attachment) {
    const url = await firstValueFrom(this.loadIntakeDocById(caseId, attachment.id));
    const fileNameParts = attachment.path.split('/');
    this.saveDocumentFromUrl(attachment.original_filename ?? fileNameParts[fileNameParts.length - 1], url);
  }

  async downloadInvoice(invoiceId: string, path: string, version: string = undefined) {
    const splitPath = path.split('/');
    const fullFilename = splitPath[splitPath.length - 1];
    const dotIndex = fullFilename.lastIndexOf('.');
    const extension = dotIndex > 0 ? fullFilename.substring(dotIndex + 1) : '';
    const filename = dotIndex > 0 ? fullFilename.substring(0, dotIndex) : fullFilename;
    const url = await firstValueFrom(this.loadInvoiceById(invoiceId, version));
    this.saveDocumentFromUrl(`${filename}.${extension}`, url);
  }

  /** Downloads one of a template's stored file VERSIONS, by version id — the key never crosses the API. */
  async loadAndSaveDocumentTemplateVersion(id: string, versionId: string, filename: string = null) {
    const url = await firstValueFrom(
      this.http.get<string>(`${this.environment.api_prefix}api/document/templates/${id}/versions/${versionId}`, { responseType: 'text' as never })
    );
    const keyName = url.split('?')[0].split('/').pop() || '';
    const name = filename?.trim() || keyName || 'download';
    this.saveDocumentFromUrl(this.withExtensionFromSignedUrl(name, url), url);
  }

  async loadAndSaveDocumentTemplate(id: string, filename: string = null) {
    const url = await firstValueFrom(this.loadDocumentTemplate(id));
    // The backend returns a presigned CloudFront URL. Strip the query string before deriving a
    // fallback name — otherwise its signature/content-disposition params land in the filename and
    // the browser's Save As dialog rejects the illegal characters (?, &, =, ").
    const keyName = url.split('?')[0].split('/').pop() || '';
    const name = filename?.trim() || keyName || 'download';
    this.saveDocumentFromUrl(this.withExtensionFromSignedUrl(name, url), url);
  }

  async generateTestDocument(formId: string, fieldData: { [key: string]: string }): Promise<void> {
    const response = await firstValueFrom(this.http.post(`${this.environment.api_prefix}api/forms/${formId}/generate-test`, fieldData, {
      responseType: 'blob',
      observe: 'response'
    }));

    // name of the file is returned from the server in CONTENT_DISPOSITION filename value
    // const contentDisposition = data.headers.get('Content-Disposition');
    const contentDisposition = response.headers.get('Content-Disposition');
    const match = /filename="?([^"]+)"?/.exec(contentDisposition);
    const filename = match ? match[1] : 'downloaded-file.docx';

    this.saveDocument(filename, response.body);
  }

  async generateTestCaseDocument(formId: string, caseId: string): Promise<void> {
    const response =
      await firstValueFrom(this.http.post(`${this.environment.api_prefix}api/forms/${formId}/generate-case-by-id`, caseId, {
        responseType: 'blob',
        observe: 'response'
      }));

    // name of the file is returned from the server in CONTENT_DISPOSITION filename value
    // const contentDisposition = data.headers.get('Content-Disposition');
    const contentDisposition = response.headers.get('Content-Disposition');
    const match = /filename="?([^"]+)"?/.exec(contentDisposition);
    const filename = match ? match[1] : 'downloaded-file.docx';

    this.saveDocument(filename, response.body);
  }

}
